/**
 * The project page's tabs (PRP-WEB-1 W2.4): Overview (default) | Floor plan | Intake |
 * Notes & photos, plus Scope only when the project has a field-scope row (today's scope grid).
 * The active tab lives in the URL (`?tab=plan|intake|notes|scope`; Overview has no param), so
 * the chat can still send the user straight to `?tab=intake` (PRP-INTAKE-2). Split out of
 * V2EstimateDetailPage.tsx to keep it under its god-file ceiling.
 */
import { useCallback } from 'react';
import type { KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { cn } from '../../lib/utils';
import type { FieldNote } from '../../types';
import { FieldNotesPanel } from './FieldNotesPanel';
import { FloorPlanView } from './FloorPlanView';
import IntakeTab from './IntakeTab';
import { IconLayers } from './icons';
import { Button } from './ui/button';
import { EmptyState } from './ui/empty-state';

/** The project page's views. */
export type PageTab = 'overview' | 'plan' | 'intake' | 'notes' | 'scope';

const TAB_LABEL: Record<PageTab, string> = {
  overview: 'Overview',
  plan: 'Floor plan',
  intake: 'Intake',
  notes: 'Notes & photos',
  scope: 'Scope',
};

/** The tab a `?tab=` value names; anything unknown is Overview. `estimate` (the old default) is Scope. */
export function tabFromParam(v: string | null): PageTab {
  if (v === 'plan' || v === 'intake' || v === 'notes' || v === 'scope') return v;
  if (v === 'estimate') return 'scope';
  return 'overview';
}

/**
 * The tab strip. Arrow keys move between tabs (WAI-ARIA tabs pattern); only the active tab is in
 * the Tab order.
 */
export function PageTabs({
  tab,
  onSelect,
  showScope = false,
}: {
  tab: PageTab;
  onSelect: (_t: PageTab) => void;
  showScope?: boolean;
}) {
  const ids: PageTab[] = ['overview', 'plan', 'intake', 'notes', ...(showScope ? (['scope'] as PageTab[]) : [])];
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = ids.indexOf(tab);
    const next = ids[(i + (e.key === 'ArrowRight' ? 1 : ids.length - 1)) % ids.length];
    onSelect(next);
    requestAnimationFrame(() => document.getElementById(`page-tab-btn-${next}`)?.focus());
  };
  return (
    <div
      role="tablist"
      aria-label="Project views"
      onKeyDown={onKey}
      className="flex flex-none gap-1 overflow-x-auto border-b border-[var(--bd-subtle)] bg-[var(--bg-1)] px-[18px]"
    >
      {ids.map((id) => (
        <button
          key={id}
          id={`page-tab-btn-${id}`}
          type="button"
          role="tab"
          aria-selected={tab === id}
          aria-controls={`page-tab-${id}`}
          tabIndex={tab === id ? 0 : -1}
          onClick={() => onSelect(id)}
          className={cn(
            '-mb-px whitespace-nowrap border-b-2 px-2 py-2 text-[12.5px] font-[560] outline-none',
            'focus-visible:rounded-[var(--r-xs)] focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
            tab === id
              ? 'border-[var(--accent)] text-[var(--tx-hi)]'
              : 'border-transparent text-[var(--tx-lo)] hover:text-[var(--tx-hi)]',
          )}
        >
          {TAB_LABEL[id]}
        </button>
      ))}
    </div>
  );
}

/** The active tab from ?tab=, and a setter that rewrites the URL in place. */
export function useProjectPageTab(): [PageTab, (_t: PageTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromParam(searchParams.get('tab'));
  const selectTab = useCallback(
    (t: PageTab) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (t === 'overview') next.delete('tab');
          else next.set('tab', t);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  return [tab, selectTab];
}

/** A tab's scrolling panel under the page header. */
function TabPanel({ id, children, wide }: { id: PageTab; children: React.ReactNode; wide?: boolean }) {
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
      role="tabpanel"
      id={`page-tab-${id}`}
      aria-labelledby={`page-tab-btn-${id}`}
    >
      <div className={cn('mx-auto w-full px-4 py-4', wide ? 'max-w-5xl' : 'max-w-3xl')}>{children}</div>
    </div>
  );
}

/** The Intake tab's panel. */
export function IntakeTabPanel({ projectId, projectName }: { projectId: string; projectName: string }) {
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
      role="tabpanel"
      id="page-tab-intake"
      aria-labelledby="page-tab-btn-intake"
    >
      <IntakeTab projectId={projectId} projectName={projectName} />
    </div>
  );
}

/** The Notes & photos tab's panel. */
export function NotesTabPanel({ projectId, notes }: { projectId: string; notes: FieldNote[] }) {
  return (
    <TabPanel id="notes">
      <FieldNotesPanel projectId={projectId} notes={notes} />
    </TabPanel>
  );
}

/** The Floor plan tab: the drawn plan and the way into the full-screen editor (W3's route). */
export function PlanTabPanel({ projectId, notes }: { projectId: string; notes: FieldNote[] }) {
  const hasPlan = notes.some((n) => n.room_geometry);
  return (
    <TabPanel id="plan" wide>
      {hasPlan ? (
        <>
          <div className="mb-2 flex justify-end">
            <Button asChild variant="primary" size="sm">
              <Link to={`/project/${encodeURIComponent(projectId)}/plan`} data-testid="open-floor-plan">
                Open floor plan
              </Link>
            </Button>
          </div>
          <FloorPlanView notes={notes} />
        </>
      ) : (
        <EmptyState icon={<IconLayers />} title="No floor plan yet" description="Rooms sketched on the phone show here." />
      )}
    </TabPanel>
  );
}
